/**
 * WarningToast — a self-contained, dismissible floating warning.
 *
 * The app has no global toast/notification system, so this is a lightweight,
 * fixed-position banner used to surface action errors (e.g. "this task isn't
 * available yet"). Pair it with `useAutoDismissWarning` for the show/auto-clear
 * state. Once an app-wide notification mechanism exists, both can be replaced.
 */

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export type ToastVariant = 'warning' | 'success' | 'error' | 'info';

const Toast = styled.div<{ $zIndex: number; $variant: ToastVariant }>`
  position: fixed;
  top: ${({ theme }) => theme.spacing[5]};
  left: 50%;
  transform: translateX(-50%);
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  max-width: 520px;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme, $variant }) => theme.colors.status[$variant].bg};
  color: ${({ theme, $variant }) => theme.colors.status[$variant].text};
  border: 1px solid ${({ theme, $variant }) => theme.colors.status[$variant].border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  button {
    margin-left: auto;
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    font-size: 18px;
    line-height: 1;
  }
`;

export interface WarningToastProps {
  /** When set, the toast is shown; when null/empty, nothing renders. */
  message: string | null;
  onDismiss: () => void;
  /** Visual style. Defaults to `warning` for backward compatibility. */
  variant?: ToastVariant;
  /** Override the stacking order (e.g. above an overlay). Defaults to 2000. */
  zIndex?: number;
}

export function WarningToast({
  message,
  onDismiss,
  variant = 'warning',
  zIndex = 2000,
}: WarningToastProps) {
  if (!message) return null;
  // Success/info are polite status updates; warning/error are assertive alerts.
  const role = variant === 'success' || variant === 'info' ? 'status' : 'alert';
  return (
    <Toast role={role} $zIndex={zIndex} $variant={variant}>
      <span>{message}</span>
      <button type="button" onClick={onDismiss} aria-label="Dismiss">
        &times;
      </button>
    </Toast>
  );
}

/**
 * Holds a transient warning message that auto-clears after `timeoutMs`.
 * `showWarning` accepts an API error (or any value) and resolves the backend
 * message, falling back to `fallback`.
 */
export function useAutoDismissWarning(fallback: string, timeoutMs = 6000) {
  const [warning, setWarning] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const showWarning = (err?: unknown) => {
    const msg =
      typeof err === 'string'
        ? err
        : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;
    // Reset any in-flight dismissal so a rapid second error isn't cleared early.
    cancelTimer();
    setWarning(msg);
    timerRef.current = setTimeout(() => setWarning(null), timeoutMs);
  };

  const clear = () => {
    cancelTimer();
    setWarning(null);
  };

  // Clean up a pending timer if the consumer unmounts.
  useEffect(() => cancelTimer, []);

  return { warning, showWarning, clear };
}

export default WarningToast;
